import { Box, Chip, Fade, Stack, Tooltip, Typography } from '@mui/material';
import { IChecklistTemplate } from '../types';

export const TemplateChecklistColumns = () => {
  return [
    {
      field: 'templateInfo',
      label: 'Template Info',
      renderCell: ({ row }: { row: IChecklistTemplate }) => (
        <Stack direction="row" alignItems="center" spacing={4}>
          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1rem' }}>
            {row.name || '-'}
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            {row.sections?.length || 0} sezioni
          </Typography>
        </Stack>
      )
    },
    {
      field: 'equipments',
      label: 'Strumenti Collegati',
      align: 'right' as const,
      renderCell: ({ row }: { row: IChecklistTemplate }) => {
        const equipmentsCount = row.equipments?.length || 0;

        if (equipmentsCount === 0) {
          return (
            <Chip
              label="Nessuno strumento"
              variant="outlined"
              sx={{
                color: '#64748b',
                backgroundColor: '#f8fafc',
                fontWeight: 500,
                fontSize: '0.85rem',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
              }}
            />
          );
        }

        // Contenuto del Tooltip: Elenco strutturato degli strumenti
        const tooltipContent = (
          <Box sx={{ p: 0.5 }}>
            <Typography
              sx={{
                fontSize: '0.725rem',
                fontWeight: 700,
                color: '#2563eb', // Matches your exact brand Blue Accent color
                mb: 1.5,
                textTransform: 'uppercase',
                letterSpacing: '0.05em' // Corrected from 'tracking' to standard MUI 'letterSpacing'
              }}>
              Elenco Strumenti ({equipmentsCount})
            </Typography>
            <Stack spacing={1} sx={{ maxWidth: 280 }}>
              {row.equipments.map((eq: any, index: number) => {
                const code = typeof eq === 'object' ? eq.code : `ID: ${eq}`;
                const description = typeof eq === 'object' ? eq.description : '';

                return (
                  <Box
                    key={eq.id || index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      pb: index !== equipmentsCount - 1 ? 1 : 0,
                      // Soft blue divider that perfectly dissolves into the #eff6ff background
                      borderBottom: index !== equipmentsCount - 1 ? '1px solid #bfdbfe' : 'none'
                    }}>
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: '#1e3a8a' // Deep Navy Blue (Blue 900) for sharp layout readability
                      }}>
                      {code}
                    </Typography>
                    {description && (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#475569', // Muted Slate Gray (Slate 600) for subtext contrast
                          fontWeight: 500,
                          mt: 0.25
                        }}>
                        {description}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="left"
            arrow
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 200 }}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#eff6ff', // Stesso sfondo azzurrato del chip (Blue 50)
                  color: '#1e40af', // Testo blu scuro ad alto contrasto per la lettura (Blue 800)
                  boxShadow:
                    '0 10px 25px -5px rgba(37, 99, 235, 0.06), 0 4px 12px -4px rgba(37, 99, 235, 0.04)', // Ombra morbida basata sulle tonalità del blu
                  borderRadius: '12px', // Angoli leggermente più morbidi per seguire l'estetica dei chip
                  padding: '12px 16px',
                  border: '1px solid #bfdbfe' // Bordo azzurro coordinato (Blue 200)
                }
              },
              arrow: {
                sx: {
                  color: '#eff6ff', // La freccia si fonde perfettamente con lo sfondo azzurrato
                  '&::before': {
                    border: '1px solid #bfdbfe' // Applica il bordo coordinato anche sulla punta della freccia
                  }
                }
              }
            }}>
            <Chip
              label={
                equipmentsCount === 1
                  ? '1 strumento collegato'
                  : `${equipmentsCount} strumenti collegati`
              }
              variant="filled"
              sx={{
                borderColor: '#e2e8f0',
                color: '#2563eb', // Colore Blue accent coerente
                backgroundColor: '#eff6ff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: '#dbeafe'
                }
              }}
            />
          </Tooltip>
        );
      }
    }
  ];
};
